/**
 * AtlasProbeLightModule — flat "inspection lighting" for headless probes.
 *
 * Software-GL screenshots (playwright + swiftshader) render the world too dark
 * to judge visually: the physically-lit ground goes near-black at most sun
 * angles, so every layout/scale question ended up unanswerable from a probe
 * frame (the 2026-07-20 city-overhaul lesson). When `localStorage['atlas:probe']
 * = '1'` this module:
 *
 *   · adds a strong ambient + hemisphere rig (flat-lights all geometry), and
 *   · suppresses scene fog (re-asserted on a timer — the weather pipeline
 *     re-applies fog on every environment update).
 *
 * Probes opt in via `page.addInitScript(() => localStorage.setItem('atlas:probe','1'))`.
 * Never active for players — the flag is deliberately not surfaced in any menu.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';

function atlasPos(): { x: number; z: number } {
  return { x: atlas.localX, z: atlas.localZ };
}

export class AtlasProbeLightModule implements IModule {
  readonly name = 'AtlasProbeLight';
  private timer: number | null = null;
  private rig: THREE.Group | null = null;

  init(app: App): void {
    let flag = '';
    try { flag = localStorage.getItem('atlas:probe') ?? ''; } catch { /* storage blocked */ }
    if (!flag) return;
    // 2026-09-09: a player's browser carried a stale 'atlas:probe' flag and ran
    // the inspection rig for real — the fog nulled every second, a 1.7 ambient
    // over every world. The rig arms only under automation (navigator.webdriver)
    // or the explicit 'force' value; a stale '1' in a real browser is cleared.
    const automated = typeof navigator !== 'undefined' && (navigator as { webdriver?: boolean }).webdriver === true;
    if (!automated && flag !== 'force') {
      try { localStorage.removeItem('atlas:probe'); } catch { /* fine */ }
      console.warn('[ProbeLight] atlas:probe was set in a real browser — cleared (set it to "force" for a headed inspection)');
      return;
    }
    const scene = app.world.scene;
    this.rig = new THREE.Group();
    this.rig.name = 'probe-light-rig';
    this.rig.add(new THREE.AmbientLight(0xffffff, 1.7));
    this.rig.add(new THREE.HemisphereLight(0xdcecff, 0x9a8f80, 1.3));
    scene.add(this.rig);
    // Fog off, and KEEP it off — weather re-applies fog on env updates.
    scene.fog = null;
    // Diagnostics channel: probes read `window.__probe` to see WHY a frame is
    // empty (chunk-streamer health, frame cadence, position) instead of
    // guessing from black pixels — the 2026-07-20 debugging lesson.
    let frames = 0;
    const tick = (): void => { frames++; requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
    let lastFrames = 0;
    this.timer = window.setInterval(() => {
      scene.fog = null;
      interface EnvInternals {
        chunkMeshes?: Map<string, unknown>; chunkBuildQueue?: unknown[];
        chunkPending?: Map<string, unknown>; chunkWorkerBroken?: boolean;
      }
      const env = app.getModule('Environment') as unknown as EnvInternals | undefined;
      interface SettleInternals {
        built?: Map<number, unknown>; building?: Set<number>; assemblyQueue?: unknown[];
        buildStage?: Map<number, string>;
      }
      const settle = app.getModule('CommonsSettlements') as unknown as SettleInternals | undefined;
      (window as unknown as Record<string, unknown>).__probe = {
        x: Math.round(atlasPos().x), z: Math.round(atlasPos().z),
        chunks: env?.chunkMeshes?.size ?? -1,
        queue: env?.chunkBuildQueue?.length ?? -1,
        pending: env?.chunkPending?.size ?? -1,
        workerBroken: env?.chunkWorkerBroken ?? null,
        settlementsBuilt: settle?.built?.size ?? -1,
        settlementsBuilding: settle?.building?.size ?? -1,
        settleQueue: settle?.assemblyQueue?.length ?? -1,
        // Names the exact stage of every in-flight settlement build — the
        // "stuck at building:1" diagnosis that took three probe cycles to
        // even localize before this existed.
        settleStages: settle?.buildStage ? [...settle.buildStage.entries()].map(([id, s]) => `${id}=${s}`) : [],
        fps2s: (frames - lastFrames) / 2,
      };
      lastFrames = frames;
    }, 2000);
    // Direct handles for probe-driven experiments (load-path bisection etc.).
    (window as unknown as Record<string, unknown>).__assets = app.getModule('AssetModule');
    (window as unknown as Record<string, unknown>).__scene = scene; // census probes group visible meshes
    (window as unknown as Record<string, unknown>).__env = app.getModule('Environment'); // versona-crowd internals
    (window as unknown as Record<string, unknown>).__atlas = atlas; // conn + local state for row-level probes
    (window as unknown as Record<string, unknown>).__app = app; // renderer/cameras/module access for cull-path probes
    console.info('[ProbeLight] headless inspection lighting + __probe diagnostics active');
  }

  dispose(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.rig?.parent?.remove(this.rig);
  }
}
